import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { FEEDBACKS } from "../data/content";

const colleagues = FEEDBACKS.filter((fb) => fb.type === "colleague");
const customers = FEEDBACKS.filter((fb) => fb.type === "customer");

/* ── Colleague card (original style) ── */
function ColleagueCard({ fb }) {
  return (
    <div id={`feedback-${fb.id}`} className="card group hover:-translate-y-1 transition-all duration-300" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {fb.embedUrl ? (
        <div className="w-full rounded-2xl overflow-hidden mb-4 relative" style={{ paddingBottom: "56.25%" }}>
          <iframe
            src={fb.embedUrl}
            title={fb.name}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
          />
        </div>
      ) : fb.thumbnail ? (
        <div className="w-full rounded-2xl overflow-hidden mb-4">
          <img
            src={fb.thumbnail}
            alt={fb.name}
            className="w-full object-cover object-top"
            style={fb.thumbnailHeight ? { height: fb.thumbnailHeight } : { height: "auto" }}
          />
        </div>
      ) : (
        <div className={`w-full h-32 rounded-2xl bg-gradient-to-br ${fb.bg} flex items-center justify-center mb-4`}>
          <span className="text-5xl">{fb.avatar}</span>
        </div>
      )}

      {fb.quote && (
        <blockquote style={{
          fontFamily: "'Space Grotesk',sans-serif", fontSize: 13,
          color: "#3F3F46", lineHeight: 1.75, margin: 0,
          fontStyle: "italic", borderLeft: "3px solid #2563EB", paddingLeft: 14,
        }}>
          "{fb.quote}"
        </blockquote>
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 14, borderTop: "1px solid #F4F4F5" }}>
        <div>
          <div style={{ fontFamily: "'Archivo',sans-serif", fontWeight: 700, fontSize: 13, color: "#09090B" }}>{fb.name}</div>
          <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 12, color: "#64748B", marginTop: 2 }}>{fb.role}</div>
        </div>
      </div>
    </div>
  );
}

/* ── Customer carousel (original style) ── */
function CustomerCarousel({ customers }) {
  const [index, setIndex] = useState(0);
  const prev = () => setIndex((i) => Math.max(i - 1, 0));
  const next = () => setIndex((i) => Math.min(i + 1, customers.length - 1));
  const fb = customers[index];

  const touchStartX = useRef(null);
  const onTouchStart = useCallback(e => { touchStartX.current = e.touches[0].clientX; }, []);
  const onTouchEnd = useCallback(e => {
    if (touchStartX.current === null) return;
    const dx = touchStartX.current - e.changedTouches[0].clientX;
    if (dx > 40) next();
    else if (dx < -40) prev();
    touchStartX.current = null;
  }, [index]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className="mb-12 flex items-center gap-3 justify-center"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <button
        onClick={prev}
        disabled={index === 0}
        style={{
          width: 44, height: 44, borderRadius: "50%", flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          border: "1.5px solid #E4E4E7", background: "#fff", cursor: "pointer",
          color: "#09090B", transition: "all 180ms",
          opacity: index === 0 ? 0.25 : 1,
        }}
        onMouseEnter={e => { e.currentTarget.style.background = "#09090B"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "#09090B"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#09090B"; e.currentTarget.style.borderColor = "#E4E4E7"; }}
      >
        <ChevronLeft size={18} />
      </button>

      <div className="max-w-sm w-full">
        <ColleagueCard fb={fb} />
        <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
          <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 12, color: "#94A3B8" }}>{index + 1} / {customers.length}</span>
        </div>
      </div>

      <button
        onClick={next}
        disabled={index === customers.length - 1}
        style={{
          width: 44, height: 44, borderRadius: "50%", flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          border: "1.5px solid #E4E4E7", background: "#fff", cursor: "pointer",
          color: "#09090B", transition: "all 180ms",
          opacity: index === customers.length - 1 ? 0.25 : 1,
        }}
        onMouseEnter={e => { e.currentTarget.style.background = "#09090B"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "#09090B"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#09090B"; e.currentTarget.style.borderColor = "#E4E4E7"; }}
      >
        <ChevronRight size={18} />
      </button>
    </div>
  );
}

/* ── Tab button (new style) ── */
function Tab({ label, count, isActive, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "7px 16px", borderRadius: 999,
        border: isActive ? "1.5px solid #09090B" : "1.5px solid #E4E4E7",
        background: isActive ? "#09090B" : "#fff",
        color: isActive ? "#fff" : "#52525B",
        fontFamily: "'Space Grotesk',sans-serif",
        fontSize: 13, fontWeight: 600,
        cursor: "pointer",
        transition: "all 180ms cubic-bezier(0.25,0.1,0.25,1)",
      }}
      onMouseEnter={e => { if (!isActive) { e.currentTarget.style.borderColor = "#09090B"; e.currentTarget.style.color = "#09090B"; } }}
      onMouseLeave={e => { if (!isActive) { e.currentTarget.style.borderColor = "#E4E4E7"; e.currentTarget.style.color = "#52525B"; } }}
    >
      {label}
      <span style={{
        fontSize: 10, fontWeight: 700,
        background: isActive ? "rgba(255,255,255,0.18)" : "#F4F4F5",
        color: isActive ? "#fff" : "#94A3B8",
        padding: "1px 5px", borderRadius: 99,
      }}>
        {count}
      </span>
    </button>
  );
}

export default function Feedback() {
  const [activeTab, setActiveTab] = useState("colleague");
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace("#", "");
    const targetFb = FEEDBACKS.find(fb => `feedback-${fb.id}` === id);
    if (targetFb) setActiveTab(targetFb.type);
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  }, [location.hash]);

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "48px 24px 80px" }}>

      {/* Header (new style) */}
      <div className="reveal" style={{ marginBottom: 40 }}>
        <span className="section-label">Feedback</span>
        <h1 style={{
          fontFamily: "'Archivo',sans-serif", fontWeight: 900,
          fontSize: "clamp(28px,4vw,52px)", color: "#09090B",
          margin: "0 0 10px", letterSpacing: "-0.03em", lineHeight: 1.05,
        }}>
          Nhận xét &amp;<br /><span style={{ color: "#2563EB" }}>Đánh giá</span>
        </h1>
        <p style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 15, color: "#64748B", margin: 0, maxWidth: 460, lineHeight: 1.65 }}>
          Những gì giảng viên và khách hàng nói về công việc của mình.
        </p>
      </div>

      {/* Tabs (new style) */}
      <div className="reveal reveal-delay-1" style={{ display: "flex", gap: 8, marginBottom: 36 }}>
        <Tab
          label="Giảng viên"
          count={colleagues.length}
          isActive={activeTab === "colleague"}
          onClick={() => setActiveTab("colleague")}
        />
        <Tab
          label="Khách hàng"
          count={customers.length}
          isActive={activeTab === "customer"}
          onClick={() => setActiveTab("customer")}
        />
      </div>

      {/* Content (original style) */}
      {activeTab === "customer" ? (
        customers.length > 0
          ? <CustomerCarousel customers={customers} />
          : <div className="text-center py-16 text-gray-400 font-sans text-sm">Chưa có feedback nào.</div>
      ) : (
        <div className="flex flex-col gap-6 mb-12">
          {colleagues.length > 0 ? (
            colleagues.map((fb) => (
              <ColleagueCard key={fb.id} fb={fb} />
            ))
          ) : (
            <div className="text-center py-16 text-gray-400 font-sans text-sm">Chưa có feedback nào.</div>
          )}
        </div>
      )}

    </div>
  );
}
